import {ErrorMessage, Form, Formik, FormikHelpers} from 'formik'
import _ from 'lodash'
import {DateTime} from 'luxon'
import {signIn} from 'next-auth/react'
import {FC, useState} from 'react'

import {ButtonStatus} from '@/components/common/content/Button'
import {Text, TextVariant} from '@/components/common/content/Text'
import {FormErrorMessage} from '@/components/common/form/FormErrorMessage'
import {FormItem} from '@/components/common/form/FormItem'
import {EmailIcon} from '@/components/common/icons/EmailIcon'
import {LockIcon} from '@/components/common/icons/LockIcon'
import {usePreferences} from '@/components/context/Preferences'
import {PasswordInput} from '@/components/inputs/PasswordInput'
import {TextInput} from '@/components/inputs/TextInput'
import {useAuthContent} from '@/content/user/authentication'
import {Link, useTranslation} from '@/i18n'
import {BUTTON_ERROR_MIN_DURATION} from '@/theme/cssTransitions'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {REST_METHODS} from '@/utils/api/types'
import {NextAuthErrors} from '@/utils/auth/constants'
import {enforceFormResultMinimumDuration, submitWithButtonStatus} from '@/utils/formikHelpers'
import {useRouter} from '@/utils/hooks/useRouter'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {fetch} from '@/utils/sentry-fetch/browser'
import {analytics} from '@/utils/tracking/analytics'
import {
  getSignInLocale,
  markSignInLanguageSwitch,
  syncUserProfileSettings,
} from '@/utils/user/client'
import {AuthActionType, USER_SECRET_TTL_MINUTES} from '@/utils/user/constants'

import {AuthModalLayout} from './AuthModalLayout'

type FormFields = {
  email: string
  password: string
}

type SignInModalProps = {
  closeModal: () => void
  setActiveAuthActionType: (authActionType: AuthActionType) => void
  onSuccessfulSignIn?: () => void
  setEmail: (email: string) => void
  setMagicLinkExpiresAt: (value: DateTime) => void
  hasSignUp?: boolean
}

export const SignInModal: FC<SignInModalProps> = ({
  closeModal,
  setActiveAuthActionType,
  onSuccessfulSignIn,
  setEmail,
  setMagicLinkExpiresAt,
  hasSignUp,
}) => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {currency, setCurrency} = usePreferences()
  const router = useRouter()

  const {
    signIn: {
      title,
      forgotPasswordLinkUrl,
      forgotPasswordLinkText,
      signUpPromoText,
      signUpLinkText,
      buttonText,
      loginWithPasswordButtonText,
      magicLink,
      errors,
      orDividerText,
    },
    emailLabel,
    emailPlaceholder,
    passwordLabel,
  } = useAuthContent()

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isMagicLinkMode, setIsMagicLinkMode] = useState(false)

  const switchMode = (magicLinkMode: boolean) => {
    setSubmitError(null)
    setIsMagicLinkMode(magicLinkMode)
  }

  const onPasswordSignIn = async (values: FormFields, {setStatus}: FormikHelpers<FormFields>) => {
    const {email, password} = values

    setStatus(ButtonStatus.submitting)

    if (submitError) {
      setSubmitError(null)
    }

    try {
      const response = await signIn<'credentials'>('credentials', {
        email,
        password,
        redirect: false,
      })

      // nextauth returns either general 401 error or 200 with custom error message
      if (response?.ok === false || response?.error) {
        setStatus(ButtonStatus.error)
        setSubmitError(response?.error ?? 'api-error-description')
        await enforceFormResultMinimumDuration(BUTTON_ERROR_MIN_DURATION)
      } else {
        const callbackUrl = _.get(router.query, 'callbackUrl') as string | undefined
        // Before anything awaits: the profile may load, and be acted on, while it does
        markSignInLanguageSwitch(callbackUrl ?? window.location.href)
        const syncResult = await syncUserProfileSettings(language, currency, setCurrency)
        if (callbackUrl) {
          await router.push(callbackUrl, {
            locale: getSignInLocale(callbackUrl, syncResult?.language),
          })
        } else {
          onSuccessfulSignIn?.()
          closeModal()
        }
      }
    } catch {
      setStatus(ButtonStatus.error)
      await enforceFormResultMinimumDuration(BUTTON_ERROR_MIN_DURATION)
    } finally {
      setStatus(ButtonStatus.idle)
    }
  }

  const onMagicLinkRequest = async (values: FormFields, {setStatus}: FormikHelpers<FormFields>) => {
    if (submitError) {
      setSubmitError(null)
    }

    await submitWithButtonStatus({
      setStatus,
      onSubmit: () =>
        fetch(routeTo.api.user.loginByMagicLinkRequest(), {
          method: REST_METHODS.POST,
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            email: values.email,
            language,
            // with its locale prefix, which `asPath` drops - without it, the link opens in English
            callbackUrl:
              router.query.callbackUrl ?? `${window.location.pathname}${window.location.search}`,
          }),
        }),
      onSuccess: ({secret_generated_at}: {secret_generated_at: string}) => {
        analytics.track('Log in by magic link requested')
        setEmail(values.email)
        setMagicLinkExpiresAt(
          DateTime.fromISO(secret_generated_at).plus({minutes: USER_SECRET_TTL_MINUTES}),
        )
      },
      onError: (errorKey) => setSubmitError(errorKey),
      errorPath: '/components/user/auth/SignInModal',
      showSuccessStatus: false,
    })
  }

  const onSubmit = (values: FormFields, formikHelpers: FormikHelpers<FormFields>) =>
    isMagicLinkMode
      ? onMagicLinkRequest(values, formikHelpers)
      : onPasswordSignIn(values, formikHelpers)

  return (
    <>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        onSubmit={onSubmit}
      >
        {({status, submitForm}) => {
          const config = isMagicLinkMode
            ? {
                title: t(magicLink.title),
                description: t(magicLink.description),
                primaryButton: {
                  text: t(magicLink.buttonText),
                  onClick: submitForm,
                  status,
                  dataTest: 'SendMagicLinkButton',
                },
                secondaryButton: {
                  text: t(loginWithPasswordButtonText),
                  onClick: () => switchMode(false),
                  dataTest: 'LoginWithPasswordButton',
                  icon: <LockIcon size={IconSize.medium} color={theme.palette.text.default} />,
                },
              }
            : {
                title: t(title),
                description: undefined,
                primaryButton: {
                  text: t(buttonText),
                  onClick: submitForm,
                  status,
                  dataTest: 'LoginButton',
                },
                secondaryButton: {
                  text: t(magicLink.title),
                  onClick: () => switchMode(true),
                  dataTest: 'SwitchToMagicLinkButton',
                  icon: <EmailIcon size={IconSize.medium} />,
                },
              }

          const {title: modalTitle, description, primaryButton, secondaryButton} = config

          return (
            <Form>
              <AuthModalLayout
                title={modalTitle}
                description={description}
                primaryButton={primaryButton}
                secondaryButton={secondaryButton}
                onCancel={closeModal}
                errorMessage={
                  submitError
                    ? submitError in errors
                      ? t(errors[submitError as keyof typeof errors])
                      : t(errors[NextAuthErrors.Default])
                    : undefined
                }
                dividerText={t(orDividerText)}
                footer={
                  <>
                    <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                      <Link href={forgotPasswordLinkUrl} legacyBehavior>
                        <a className="underline">{t(forgotPasswordLinkText)}</a>
                      </Link>
                    </Text>
                    {hasSignUp && (
                      <div className="note">
                        <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                          {t(signUpPromoText)}
                          <button
                            onClick={() => setActiveAuthActionType(AuthActionType.signUp)}
                            type="button"
                            className="signup-button"
                          >
                            <Text
                              variant={TextVariant.text14}
                              element="span"
                              color={theme.palette.text.light}
                              className="underline"
                            >
                              {t(signUpLinkText)}
                            </Text>
                          </button>
                        </Text>
                      </div>
                    )}
                  </>
                }
              >
                <FormItem label={t(emailLabel)}>
                  <ErrorMessage name="email" render={(msg) => <FormErrorMessage message={msg} />} />
                  <TextInput
                    name="email"
                    type="email"
                    hasError={Boolean(submitError)}
                    placeholder={t(emailPlaceholder)}
                  />
                </FormItem>
                {!isMagicLinkMode && (
                  <FormItem label={t(passwordLabel)}>
                    <ErrorMessage
                      name="password"
                      render={(msg) => <FormErrorMessage message={msg} />}
                    />
                    <PasswordInput name="password" hasError={Boolean(submitError)} />
                  </FormItem>
                )}
              </AuthModalLayout>
            </Form>
          )
        }}
      </Formik>

      <style jsx>{`
        .note {
          margin-top: ${theme.spacing[2]};
        }

        .signup-button {
          margin-left: ${theme.spacing[1]};
        }
      `}</style>
    </>
  )
}
